import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { listWinners, adminUpdateWinner, adminDeleteWinner, adminWipeWinners } from '@/lib/api';
import { formatDateShort } from '@/lib/format';

const AdminWinners = () => {
  const [items, setItems] = useState([]);
  const [wiping, setWiping] = useState(false);
  const load = () => listWinners().then((d) => setItems(d.items || []));
  useEffect(() => { load(); }, []);

  const save = async (w, patch) => {
    try { await adminUpdateWinner(w.id, patch); toast.success('Updated'); load(); }
    catch { toast.error('Update failed'); }
  };

  const removeOne = async (w) => {
    if (!window.confirm(`Delete winner "${w.winner_name_first}" (${w.prize})? This cannot be undone.`)) return;
    try {
      await adminDeleteWinner(w.id);
      toast.success('Winner deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  const wipeAll = async () => {
    const confirmText = window.prompt(
      `This will permanently delete ALL ${items.length} winner${items.length === 1 ? '' : 's'}.\n\nType DELETE to confirm.`
    );
    if (confirmText !== 'DELETE') {
      if (confirmText !== null) toast.error('Wipe cancelled — you must type DELETE');
      return;
    }
    setWiping(true);
    try {
      const res = await adminWipeWinners();
      toast.success(`Removed ${res.deleted} winner${res.deleted === 1 ? '' : 's'}`);
      load();
    } catch { toast.error('Wipe failed'); }
    finally { setWiping(false); }
  };

  return (
    <div data-testid="admin-winners-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl">Winners</h1>
          <p className="mt-2 text-sm text-muted-sage">
            {items.length === 0 ? 'No winners yet.' : `${items.length} winner${items.length === 1 ? '' : 's'} on record.`}
          </p>
        </div>
        {items.length > 0 && (
          <button
            onClick={wipeAll}
            disabled={wiping}
            data-testid="admin-winners-wipe-all"
            className="inline-flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-2 text-sm font-mono uppercase tracking-wide hover:bg-red-500/20 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {wiping ? 'Wiping…' : 'Wipe all winners'}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="mt-8 bg-surface border border-pd rounded-2xl p-8 max-w-2xl" data-testid="admin-winners-empty">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--pd-gold)]">Launch mode</div>
          <h2 className="font-display text-2xl mt-2">No winners yet</h2>
          <p className="mt-3 text-sm text-muted-sage">
            Winners will appear here automatically after you run a draw on a competition.
            Once the first real draw is complete, the winner card can be edited and featured on the homepage.
          </p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((w) => (
            <div key={w.id} className="bg-surface border border-pd rounded-2xl p-5" data-testid={`admin-winner-card-${w.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-[10px] text-muted-sage uppercase">{formatDateShort(w.drawn_at)} • {w.category}</div>
                  <div className="font-display text-2xl mt-1">{w.prize}</div>
                  <div className="font-italic-serif text-lg mt-1">{w.winner_name_first}, {w.location}</div>
                </div>
                <button
                  onClick={() => removeOne(w)}
                  data-testid={`admin-winner-delete-${w.id}`}
                  aria-label={`Delete winner ${w.winner_name_first}`}
                  className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-2.5 py-1.5 text-xs hover:bg-red-500/20"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
              <label className="block mt-3">
                <div className="font-mono text-[10px] uppercase text-muted-sage mb-1">Quote</div>
                <textarea defaultValue={w.quote} onBlur={(e) => e.target.value !== w.quote && save(w, { quote: e.target.value })} className="w-full rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 py-2 text-sm" rows={3} />
              </label>
              <label className="block mt-3">
                <div className="font-mono text-[10px] uppercase text-muted-sage mb-1">Photo URL</div>
                <input defaultValue={w.photo_url} onBlur={(e) => e.target.value !== w.photo_url && save(w, { photo_url: e.target.value })} className="w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm" />
              </label>
              <label className="flex items-center gap-2 text-sm mt-3">
                <input type="checkbox" defaultChecked={!!w.featured} onChange={(e) => save(w, { featured: e.target.checked })} className="accent-[color:var(--pd-accent-yellow)]" />
                Featured on homepage
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
export default AdminWinners;
